/**
 * 船体姿态：由四个采样点拟合 heave / pitch / roll。
 *
 * 不用物理浮力（Rapier）：浮力需要逐帧力积分，两端 tick 与浮点路径都不同，
 * 姿态会慢慢漂开，最后两条船看起来坐在不同的浪上。
 * 解析采样是「同一个 t → 同一个姿态」，且零物理开销。
 */

import { waveHeight, type Wave } from './waves.js';

export interface BoatPose {
  /** 垂直起伏（米） */
  heave: number;
  /** 绕 X 轴：船头抬起为正 */
  pitch: number;
  /** 绕 Z 轴：右舷抬起为正 */
  roll: number;
}

export interface BoatSize {
  /** 船长（沿 Z，船头在 -Z） */
  length: number;
  /** 船宽（沿 X） */
  width: number;
}

export const DEFAULT_BOAT_SIZE: BoatSize = { length: 4.6, width: 1.9 };

const tmpPose: BoatPose = { heave: 0, pitch: 0, roll: 0 };

/**
 * @param time 世界时间（秒），两端必须来自同一条时间基准
 */
export function sampleBoatPose(
  waves: readonly Wave[],
  time: number,
  size: BoatSize = DEFAULT_BOAT_SIZE,
  out: BoatPose = tmpPose,
): BoatPose {
  const hl = size.length * 0.5;
  const hw = size.width * 0.5;

  const bow = waveHeight(waves, 0, -hl, time); // 船头在 -Z
  const stern = waveHeight(waves, 0, hl, time);
  const port = waveHeight(waves, -hw, 0, time); // 左舷 -X
  const starboard = waveHeight(waves, hw, 0, time);

  out.heave = (bow + stern + port + starboard) * 0.25;
  // 绕 X 轴正转 → -Z 端（船头）向上
  out.pitch = Math.atan2(bow - stern, size.length);
  // 绕 Z 轴正转 → +X 端（右舷）向上
  out.roll = Math.atan2(starboard - port, size.width);
  return out;
}

/**
 * 舒适度：把船体摇摆按比例混合到相机上。
 * 0 = 完全平稳（地平线锁死），1 = 完全跟随船体。
 */
export function blendPoseForComfort(pose: BoatPose, comfort: number, out: BoatPose = tmpPose): BoatPose {
  const k = Math.min(1, Math.max(0, comfort));
  // 归一化 -0：(-0 === 0) 为真，返回字面量 0，避免下游拿到 -0 做位运算/序列化时出现意外
  const nz = (v: number): number => (v === 0 ? 0 : v);
  out.heave = nz(pose.heave * k);
  out.pitch = nz(pose.pitch * k);
  out.roll = nz(pose.roll * k);
  return out;
}
